/* eslint-disable */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text, TextLink } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-common';
import { SmallFiveStarRating } from '../../common/FiveStarRating/SmallFiveStarRating';
import type { DealerReview } from '../../../types';
import { DealerResponse } from './DealerResponse';
import { messages } from '../../../i18n/messages';

const CHAR_CUTOFF = 160;

const CustomerReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const HeaderRowContainer = styled.div`
  display: grid;
  grid-template-columns: max-content min-content;
  justify-content: space-between;
  align-items: center;
  row-gap: 8px;
`;

const PartialReviewStyled = styled(Text)<{ isTextCutOff: boolean }>`
  display: ${(props) => (props.isTextCutOff ? 'block' : 'none')};
`;

const FullReviewStyled = styled(Text)<{ isTextCutOff: boolean }>`
  display: ${(props) => (props.isTextCutOff ? 'none' : 'block')};
`;

const ReadMoreButtonStyled = styled<typeof TextLink>(TextLink)`
  margin-top: ${SYS_SPACE_FIXED_100};
  width: auto;
  height: 20px;
  align-items: end;
  text-decoration: none;
  min-width: auto;
  border: none;
  padding: 0;
`;

const DateTextStyled = styled(Text)`
  word-break: keep-all;
`;

interface CustomerReviewProps {
  review: DealerReview;
}

export const CustomerReview = ({ review }: CustomerReviewProps) => {
  const [isTextCutOff, setIsTextCutOff] = useState(false);
  const onReviewButtonClick = () => {
    setIsTextCutOff(false);
  };
  useEffect(() => {
    if (review.text.length > CHAR_CUTOFF) {
      setIsTextCutOff(true);
    }
  }, []);
  const readMoreLabel = useI18n(messages.five_star_read_more);
  const readMoreButton = (() => {
    return (
      <>
        ...&nbsp;
        <ReadMoreButtonStyled
          variant="standalone"
          icon="add"
          iconPosition="leading"
          size="md"
          onClick={onReviewButtonClick}
        >
          {readMoreLabel}
        </ReadMoreButtonStyled>
      </>
    );
  })();
  const partialReview = (() => {
    if (isTextCutOff) {
      const cutReview = review.text.slice(0, CHAR_CUTOFF);
      if (review.text[CHAR_CUTOFF + 1] === ' ') {
        return cutReview;
      }
      const lastWhiteSpaceIndex = cutReview.lastIndexOf(' ');
      return cutReview.slice(0, lastWhiteSpaceIndex);
    }
    return '';
  })();
  const categoryText =
    (review.categoryKey === 'SALES' && useI18n(messages.five_star_sales_category)) ||
    useI18n(messages.five_star_service_category);
  return (
    <CustomerReviewContainer>
      <HeaderRowContainer>
        <Text variant={{ order: '2', style: 'bold', type: 'copy' }}>{categoryText}</Text>
        <DateTextStyled variant={{ order: '2', style: 'bold', type: 'copy' }}>
          {review.date.toLocaleDateString()}
        </DateTextStyled>
        <SmallFiveStarRating rating={review.rating} />
      </HeaderRowContainer>
      <PartialReviewStyled
        variant={{ order: '1', style: 'normal', type: 'copy' }}
        isTextCutOff={isTextCutOff}
      >
        {partialReview}
        {readMoreButton}
      </PartialReviewStyled>
      <FullReviewStyled
        variant={{ order: '1', style: 'normal', type: 'copy' }}
        isTextCutOff={isTextCutOff}
      >
        {review.text}
      </FullReviewStyled>
      {review.dealerResponse && <DealerResponse {...review.dealerResponse} />}
    </CustomerReviewContainer>
  );
};
